<template>
    <!-- Benefits Mobile Card -->
    <article @click="handleCardClick" :class="{ 'cursor-pointer': !benefit.isLocked, 'cursor-default': benefit.isLocked }">
        <!-- Card Header with Benefit Info -->
        <header class="w-full flex items-center gap-4 justify-between" :class="{ 'opacity-50': benefit.isLocked }">
            <span class="flex items-center gap-3 min-w-0 flex-1">
                <!-- BenefitIcon - same for locked and unlocked, opacity handled by parent header -->
                <div class="relative shrink-0">
                    <BenefitIcon
                        :category="benefit.benefit.category"
                        :title="benefit.benefit.benefit_title"
                        :description="benefit.benefit.benefit_description"
                        svg-class="w-6 h-6 text-slate-600 dark:text-slate-300"
                        class="w-12 h-12"
                    />
                    <!-- MY1-3084: hidden state, as on the desktop row and a hidden
                         transaction in My Spending -->
                    <span
                        v-if="benefit.user_override?.is_hidden === true"
                        class="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full border-2 border-white bg-slate-800 text-white shadow-lg"
                        aria-label="Benefit hidden from list"
                    >
                        <Icon icon="eye-off" class="w-2 h-2" />
                    </span>
                </div>

                <!-- Benefit Title -->
                <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                        <h2
                            class="text-base font-medium truncate"
                            :class="
                                benefit.is_active === false
                                    ? 'text-slate-400'
                                    : 'text-slate-800 dark:text-slate-100'
                            "
                        >
                            {{ benefit.benefit.benefit_title }}
                        </h2>
                        <!-- Inactive badge -->
                        <span
                            v-if="benefit.is_active === false"
                            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 shrink-0"
                        >
                            Inactive
                        </span>
                        <!-- First Year Notice Icon -->
                        <Tooltip
                            v-if="benefit.benefit.show_first_year_notice"
                            x-placement="center"
                            y-placement="top"
                            class="shrink-0"
                        >
                            <template #trigger>
                                <Icon
                                    icon="info-circle"
                                    class="w-4 h-4 text-orange-500 cursor-help"
                                />
                            </template>
                            This benefit is only available after your card's
                            first anniversary
                        </Tooltip>
                    </div>
                    <div
                        v-if="
                            !showIneligibleMessage &&
                            benefit.is_active !== false
                        "
                        class="py-1 px-2 bg-slate-100 dark:bg-slate-700 rounded-lg w-fit"
                    >
                        <!-- Always active badge -->
                        <p
                            v-if="isAlwaysActive"
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            Always active
                        </p>
                        <!-- NCRED value badge -->
                        <p
                            v-else-if="isNcred"
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            {{ displayAmount }} {{ filters.pluralizeUnit(displayAmount, benefit.benefit?.values_summary?.unit_label || 'night credits') }}/{{
                                store.getPeriodTypeFormat(
                                    benefit.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                        <!-- Monetary value badge -->
                        <p
                            v-else-if="!isCountBased"
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            Up to
                            {{ filters.formatSmartMoney(displayAmount) }}/
                            {{
                                store.getPeriodTypeFormat(
                                    benefit.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                        <!-- Count-based badge -->
                        <p
                            v-else
                            class="text-slate-500 dark:text-slate-400 font-medium rounded text-sm"
                        >
                            {{ benefit.benefit?.value_details?.count || 0 }} per
                            {{
                                store.getPeriodTypeFormat(
                                    benefit.benefit.period_type,
                                    'noun'
                                )
                            }}
                        </p>
                    </div>
                </div>
            </span>

            <div class="flex items-center gap-2 shrink-0">
                <BenefitActions
                    :benefit="benefit"
                    @add-usage="handleAddUsage"
                    @view-details="handleViewDetails"
                />
            </div>
        </header>

        <!-- Card Details -->
        <div class="w-full">
            <!-- Usage Summary -->
            <div
                v-if="showIneligibleMessage"
                class="text-sm text-slate-500 dark:text-slate-400 font-normal mb-1.5"
            >
                Ineligible until {{ store.formatYear(benefit.period_end_date) }}
            </div>
            <div
                v-else-if="benefit.is_active !== false && !isAlwaysActive"
                class="flex justify-between items-center mb-1.5"
                :class="{ 'opacity-50': benefit.isLocked }"
            >
                <!-- Usage Amount -->
                <span class="text-slate-800 dark:text-slate-100 text-sm font-normal">
                    <template v-if="isNcred">
                        {{ currentPeriodUsage }}
                        of
                        {{ displayAmount }}
                        {{ filters.pluralizeUnit(displayAmount, liveBenefit.benefit?.values_summary?.unit_label || 'night credits') }}
                    </template>
                    <template v-else-if="!isCountBased">
                        {{ filters.formatSmartMoney(currentPeriodUsage) }}
                        of
                        {{ filters.formatSmartMoney(displayAmount) }}
                    </template>
                    <template v-else>
                        {{ currentPeriodUsage }}
                        of
                        {{ liveBenefit.benefit?.value_details?.count || 0 }}
                    </template>
                </span>

                <!-- Time and Period Info -->
                <div class="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                    <span>
                        {{
                            expirationTime === 'Today'
                                ? 'Expires today'
                                : `${expirationTime} left`
                        }}
                    </span>
                    <div class="flex items-center gap-1">
                        <Icon icon="arrows-repeat" class="w-4 h-4" />
                        <span>
                            {{ formattedPeriodType }}
                        </span>
                    </div>
                </div>
            </div>
            <!-- Usage Bar (hidden for always active benefits) -->
            <div
                v-if="!showIneligibleMessage && benefit.is_active !== false && !isAlwaysActive"
                class="flex items-center mb-5"
                :class="{ 'opacity-50': benefit.isLocked }"
            >
                <div class="w-full rounded-full h-1 relative" :class="benefit.isLocked ? 'bg-slate-200' : 'bg-orange-200'">
                    <div
                        class="bg-10x-orange h-1 rounded-full transition-all"
                        :style="{
                            width: calculateUsagePercentage() + '%',
                        }"
                    ></div>
                    <!-- 100% Completion Indicator -->
                    <div
                        v-if="calculateUsagePercentage() >= 100"
                        class="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-10x-orange rounded-full flex items-center justify-center border-2 border-white"
                    >
                        <Icon
                            icon="check"
                            svgClass="w-2 h-2 text-white"
                            class="text-white"
                        />
                    </div>
                </div>
            </div>
            <!-- Card -->
            <div
                class="flex items-center gap-2 min-w-0"
                v-if="viewMode !== 'by_card'"
                :class="{ 'opacity-50': benefit.isLocked }"
            >
                <ImageLoader class="shrink-0">
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${benefit.card?.image?.path}/_small/${benefit.card?.image?.file_name}`"
                            class="w-6 h-4 rounded-sm"
                            :alt="benefit.card?.title"
                        />
                    </template>
                    <template #error>
                        <div
                            class="w-6 h-4 bg-slate-200 rounded-sm flex items-center justify-center"
                        >
                            <Icon icon="credit-card" class="h-3 w-3" />
                        </div>
                    </template>
                </ImageLoader>
                <div
                    class="flex items-center gap-1 min-w-0 text-sm text-slate-800 dark:text-slate-100"
                >
                    <span class="truncate font-medium">
                        {{ benefit.card.title }}
                    </span>
                    <span
                        v-if="
                            benefit.connected_account_last_four && showLastFour
                        "
                        class="text-slate-500 dark:text-slate-400 text-xs font-normal shrink-0"
                    >
                        ({{ benefit.connected_account_last_four }})
                    </span>
                </div>
            </div>

            <!-- MY1-3079: auto-tracking provenance -->
            <BenefitAutoTrackingLabel
                :auto-tracking="benefit.auto_tracking"
                compact
                class="mt-1"
                :class="{ 'opacity-50': benefit.isLocked }"
            />

            <!-- User/Entity -->
            <div
                class="flex items-center gap-2 mt-2"
                v-if="viewMode === 'flat' && benefit.user"
                :class="{ 'opacity-50': benefit.isLocked }"
            >
                <div
                    class="flex h-5 w-5 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700"
                >
                    <Icon
                        :icon="benefit.user?.is_business ? 'business' : 'user'"
                        class="w-3 h-3 text-slate-500"
                    />
                </div>
                <div class="text-slate-800 dark:text-slate-100 text-sm font-normal">
                    {{ benefit.user?.name || 'Unknown User' }}
                </div>
            </div>

            <!-- Locked state: Upgrade to track button -->
            <button
                v-if="benefit.isLocked && !hideUpgradeButton"
                class="flex w-full items-center justify-center gap-2 mt-4 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
                @click.stop="openUpgradeModal"
            >
                <Icon icon="lock" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <span>Upgrade to track</span>
            </button>

            <!-- Sub-benefits expand section -->
            <template v-if="hasSubBenefits">
                <button
                    class="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 mt-3 transition-colors"
                    @click.stop="isSubBenefitsExpanded = !isSubBenefitsExpanded"
                >
                    <Icon
                        icon="chevron-right"
                        svgClass="w-3 h-3 transition-transform"
                        :class="{ 'rotate-90': isSubBenefitsExpanded }"
                    />
                    {{ isSubBenefitsExpanded ? 'Hide' : 'Show' }} sub-benefits
                </button>

                <div v-if="isSubBenefitsExpanded" class="mt-3 space-y-3">
                    <div
                        v-for="sub in liveBenefit.sub_benefits"
                        :key="sub.id"
                        class="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3"
                    >
                        <div class="flex items-center justify-between mb-1.5">
                            <span class="text-sm font-medium text-slate-700 dark:text-slate-200">{{ sub.benefit_title }}</span>
                            <button
                                class="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-700"
                                @click.stop="openSubBenefitModal(sub)"
                            >
                                <Icon icon="plus" svgClass="w-3 h-3" />
                                Log
                            </button>
                        </div>
                        <div class="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
                            <template v-if="sub.values_summary?.is_count_based">
                                {{ subCreditsUsed(sub) }} of {{ sub.value_details?.count || 0 }} {{ sub.value_details?.count_label || 'credits' }} used
                            </template>
                            <template v-else>
                                {{ filters.formatSmartMoney(sub.current_period_usage) }} of {{ filters.formatSmartMoney(sub.amount) }}
                            </template>
                        </div>
                        <div class="w-full bg-orange-200 rounded-full h-1">
                            <div
                                class="bg-10x-orange h-1 rounded-full"
                                :style="{ width: `${subUsagePercent(sub)}%` }"
                            ></div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </article>

    <!-- Sub-benefit add usage modal (mobile) -->
    <AddUsageModalMobile
        v-if="showSubBenefitModal && activeSubBenefit"
        :show-add-usage-modal="showSubBenefitModal"
        :selected-benefit="subBenefitItemShape"
        @close="showSubBenefitModal = false"
        @add="handleSubBenefitSave"
    />
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import dayjs from 'dayjs';
import filters from '@/filters';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Icon from '@/components/ui/icon/icon.vue';
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';
import BenefitActions from '@/components/card-benefits/benefit-actions.vue';
import Tooltip from '@/components/tooltip.vue';
import BenefitAutoTrackingLabel from '@/components/card-benefits/benefit-auto-tracking-label.vue';
import AddUsageModalMobile from '@/components/card-benefits/add-usage-modal-mobile.vue';

const props = defineProps({
    index: {
        type: Number,
        default: 0,
    },
    benefit: {
        type: Object,
        required: true,
    },
    hideUpgradeButton: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['add-usage', 'view-details']);

const cdnUrl = ref(window.cdnUrl);

const store = useCardBenefitsStore();
const { viewMode, showLastFour } = storeToRefs(store);

// Get live benefit from store to ensure reactivity to optimistic updates
const liveBenefit = computed(() => {
    return (
        store.benefits.find(
            (b) =>
                b.benefit.id === props.benefit.benefit.id &&
                b.connected_account_id === props.benefit.connected_account_id
        ) || props.benefit
    );
});

const expirationTime = computed(() => {
    // Don't show expiration for quinquennial/quadrennial TSA benefits with no usage records
    // Annual TSA benefits like CLEAR should still show expiration
    const category = props.benefit?.benefit?.category?.toLowerCase();
    const periodType = props.benefit?.benefit?.period_type?.toLowerCase();
    const usageRecords = props.benefit?.usage_records || [];
    if (
        category === 'tsa' &&
        (periodType === 'quinquennial' || periodType === 'quadrennial') &&
        usageRecords.length === 0
    ) {
        return null;
    }

    const periodEndDate = props.benefit?.period_end_date;

    if (!periodEndDate) {
        return null;
    }

    const endDate = dayjs(periodEndDate);
    const now = dayjs();

    // If the date has passed, don't show anything
    if (endDate.isBefore(now, 'day')) {
        return null;
    }

    const diffInDays = endDate.diff(now, 'day');
    const diffInMonths = endDate.diff(now, 'month');

    // For monthly benefits, always show in days
    if (periodType === 'monthly' || periodType === 'MONTHLY') {
        if (diffInDays === 0) return 'Today';
        if (diffInDays === 1) return '1 day';
        return `${diffInDays} days`;
    }

    // For annual/semiannual benefits
    if (
        periodType === 'annual' ||
        periodType === 'ANNUAL' ||
        periodType === 'semiannual' ||
        periodType === 'SEMIANNUAL'
    ) {
        // If more than 1 month away, show in months
        if (diffInMonths > 1) {
            return `${diffInMonths} months`;
        }

        // If 1 month or less, show in days
        if (diffInDays === 0) return 'today';
        if (diffInDays === 1) return '1 day';
        return `${diffInDays} days`;
    }

    // For quarterly or other types, show in days if less than a month, otherwise months
    if (diffInMonths >= 1) {
        return diffInMonths === 1 ? '1 month' : `${diffInMonths} months`;
    }

    if (diffInDays === 0) return 'today';
    if (diffInDays === 1) return '1 day';
    return `${diffInDays} days`;
});

const displayAmount = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    return store.calculateDisplayAmount(benefit);
});

const isCountBased = computed(() => {
    const benefit = props.benefit.benefit;
    return store.checkIsCountBased(benefit);
});

const isNcred = computed(
    () => props.benefit?.benefit?.values_summary?.currency === 'NCRED'
);

// Check if benefit is "always active" (no monetary value and no count)
const isAlwaysActive = computed(() => {
    const benefit = props.benefit.benefit;
    const amount = parseFloat(benefit?.amount || 0);
    const count = parseFloat(benefit?.value_details?.count || 0);
    const isCountBased = benefit?.values_summary?.is_count_based;
    
    // Always active: no dollar amount AND (not count-based OR count is 0)
    return amount === 0 && (!isCountBased || count === 0);
});

const showIneligibleMessage = computed(() => {
    return (
        !parseFloat(displayAmount.value) &&
        props.benefit.benefit.reset_basis === 'usage'
    );
});

// Calculate current period usage on frontend to handle multi-year benefits correctly
const currentPeriodUsage = computed(() => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    const recalculated = store.recalculateCurrentPeriodUsage(benefit);
    return recalculated.current_period_usage || 0;
});

const calculateUsagePercentage = () => {
    const benefit = liveBenefit.value;
    if (!benefit) return 0;

    let maxValue;
    if (isCountBased.value) {
        maxValue = benefit.benefit?.value_details?.count || displayAmount.value || 0;
    } else {
        maxValue = displayAmount.value;
    }
    const usedValue = currentPeriodUsage.value;

    return maxValue > 0 ? Math.round((usedValue / maxValue) * 100) : 0;
};

const formattedPeriodType = computed(() => {
    const periodType =
        store.formatPeriodType(props.benefit.benefit.period_type) || 'monthly';
    const resetBasis = props.benefit.benefit.reset_basis;

    if (resetBasis === 'card_anniversary') {
        return 'Cardmember year';
    }

    return (
        periodType.charAt(0).toUpperCase() + periodType.slice(1).toLowerCase()
    );
});

const handleCardClick = () => {
    // Don't open sidebar for locked benefits
    if (props.benefit.isLocked) {
        return;
    }
    emit('view-details', props.benefit);
};

const handleAddUsage = (benefit) => {
    emit('add-usage', benefit);
};

const handleViewDetails = (benefit) => {
    emit('view-details', benefit);
};

// Open upgrade modal for locked benefits
const openUpgradeModal = () => {
    store.setShowUpgradeModal(true);
};

// Sub-benefit support
const isSubBenefitsExpanded = ref((props.benefit?.sub_benefits?.length ?? 0) > 0);
const activeSubBenefit = ref(null);
const showSubBenefitModal = ref(false);

const hasSubBenefits = computed(() => (liveBenefit.value?.sub_benefits?.length ?? 0) > 0);

const subCreditsUsed = (sub) => {
    const perUnit = parseFloat(sub.amount || 0);
    if (sub.values_summary?.is_count_based && perUnit > 0) {
        return Math.floor(parseFloat(sub.current_period_usage || 0) / perUnit);
    }
    return parseFloat(sub.current_period_usage || 0);
};

const subUsagePercent = (sub) => {
    const max = sub.values_summary?.is_count_based
        ? parseFloat(sub.value_details?.count || 0)
        : parseFloat(sub.amount || 0);
    const used = sub.values_summary?.is_count_based ? subCreditsUsed(sub) : parseFloat(sub.current_period_usage || 0);
    return max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
};

const subBenefitItemShape = computed(() => {
    if (!activeSubBenefit.value) {
        return null;
    }
    const sub = activeSubBenefit.value;
    return {
        benefit: {
            card_benefit_id: sub.id,
            benefit_title: sub.benefit_title,
            amount: sub.amount,
            period_type: sub.period_type,
            value_details: sub.value_details,
            values_summary: sub.values_summary,
        },
        connected_account_id: props.benefit.connected_account_id,
        current_period_usage: sub.current_period_usage,
        usage_records: sub.usage_records || [],
        _is_sub_benefit: true,
        _sub_benefit_ref: sub,
    };
});

const openSubBenefitModal = (sub) => {
    activeSubBenefit.value = sub;
    showSubBenefitModal.value = true;
};

const handleSubBenefitSave = async (usageData) => {
    const sub = activeSubBenefit.value;
    if (!sub) {
        return;
    }
    const success = await store.handleSubBenefitUsageSubmit(
        usageData,
        sub,
        props.benefit.connected_account_id
    );
    if (success) {
        showSubBenefitModal.value = false;
        activeSubBenefit.value = null;
    }
};
</script>
